import { queryOptions, useQuery } from "@tanstack/react-query";

import data from "../../assets/mocks/auction-market-car-transactions.json";
import { getTransactionDataFiltered } from "../auctions/utils";
import { normalizeTransactionsList } from "../market-car/normalize";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { AuctionCarTransaction } from "@/types/auctions";
import type { PaginatedList } from "@/types/entity";
import type { TransactionQueryParams } from "../auctions/types";

if (import.meta.env.MODE === "test") {
  // Mocking the API response for auction cars
  const mock = AxiosMock.getInstance();
  const uri = new RegExp("^\/carburate\/ad_listings\/market_cars\/.+\/transactions\/(\\?.*)?$");
  mock.onGet(uri).reply(200, data);
}

type MarketCarQueryParams = TransactionQueryParams & {
  marketCarId: string;
};

export const getAdListingMarketCarTransactions = ({
  marketCarId,
  page = 1,
  pageSize = 25,
}: MarketCarQueryParams) => {
  return api.get(
    `/carburate/ad_listings/market_cars/${marketCarId}/transactions/?page=${page}&page_size=${pageSize}`
  ) as Promise<PaginatedList<AuctionCarTransaction>>;
};

export const getAdListingMarketCarTransactionsQueryOptions = ({
  marketCarId,
  page,
  pageSize,
}: MarketCarQueryParams) => {
  return queryOptions({
    queryKey: ["market-car-ad-listings", marketCarId, "transactions", { page, pageSize }],
    queryFn: () => getAdListingMarketCarTransactions({ marketCarId, page, pageSize }),
  });
};

type UseAdListingMarketCarTransactionsOptions = MarketCarQueryParams & {
  enabled?: boolean;
  queryConfig?: QueryConfig<typeof getAdListingMarketCarTransactions>;
};

export const useAdListingMarketCarTransactions = ({
  marketCarId,
  page,
  pageSize,
  parameters,
  enabled = true,
  queryConfig,
  sort,
}: UseAdListingMarketCarTransactionsOptions) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<AuctionCarTransaction>;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken() && enabled,
    ...getAdListingMarketCarTransactionsQueryOptions({
      marketCarId,
      page: currentPage,
      pageSize: currentPageSize,
    }),
    ...queryConfig,
  });

  data = response!;

  if (isSuccess && response) {
    data = normalizeTransactionsList(response);
    if (isClientPagination) {
      data = getTransactionDataFiltered(data, { page, pageSize, parameters, sort });
    }
  }

  return { data, isSuccess, ...props };
};
